"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { api, ApiError, type SessionRecord } from "@/lib/api";
import { loginUrl } from "@/lib/loginUrl";
import { Skeleton } from "@/components/Skeleton";

interface State {
  loading: boolean;
  sessions: SessionRecord[];
  error: string | null;
  revokingId: string | null;
}

/**
 * STORY-111 phase 1 — list active sessions for the current user; allow
 * revoking any of them. STORY-111b added the "this device" marker on
 * the row matching the access JWT's `sid` claim, so the user can tell
 * which session is their current one. Sessions minted before
 * STORY-111b have no sid in the access JWT; the marker just doesn't
 * appear in that case (graceful degrade).
 */
export function SessionsList() {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({
    loading: true,
    sessions: [],
    error: null,
    revokingId: null,
  });
  const router = useRouter();

  useEffect(() => {
    let cancelled = false;
    void load();
    async function load() {
      try {
        const sessions = await api.listMySessions();
        if (!cancelled) {
          setState({ loading: false, sessions, error: null, revokingId: null });
        }
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false,
          sessions: [],
          error:
            err instanceof Error ? err.message : t("sessionsList.loadFailed"),
          revokingId: null,
        });
      }
    }
    return () => {
      cancelled = true;
    };
    // `t` is stable per react-i18next contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleRevoke(id: string) {
    const confirmed = window.confirm(t("sessionsList.confirmRevoke"));
    if (!confirmed) return;
    setState((s) => ({ ...s, revokingId: id }));
    try {
      await api.revokeSession(id);
      setState((s) => ({
        ...s,
        sessions: s.sessions.filter((sess) => sess.id !== id),
        revokingId: null,
      }));
    } catch (err) {
      console.error("[revokeSession]", err);
      // If the API returns 401 (because we just revoked our own session),
      // bounce to /login.
      if (err instanceof ApiError && err.status === 401) {
        router.push(loginUrl());
        return;
      }
      setState((s) => ({
        ...s,
        revokingId: null,
        error:
          err instanceof Error ? err.message : t("sessionsList.revokeFailed"),
      }));
    }
  }

  if (state.loading) {
    return (
      <div
        role="status"
        aria-label={t("sessionsList.loadingAria")}
        className="space-y-2"
      >
        <Skeleton className="h-12 w-full rounded-md" />
        <Skeleton className="h-12 w-full rounded-md" />
        <Skeleton className="h-12 w-full rounded-md" />
      </div>
    );
  }
  if (state.error) {
    return (
      <p className="text-xs" style={{ color: "var(--color-no)" }}>
        {state.error}
      </p>
    );
  }
  if (state.sessions.length === 0) {
    return (
      <p className="text-xs text-[var(--color-text3)]">
        {t("sessionsList.empty")}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {state.sessions.map((s) => {
        const ua = s.userAgent ?? "";
        const browser = inferBrowser(ua);
        const os = inferOs(ua);
        const label =
          s.deviceLabel ?? t("sessionsList.browserOn", { browser, os });
        return (
          <li
            key={s.id}
            className="flex items-center justify-between gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-semibold">
                {label}
                {s.current ? (
                  <span
                    className="ml-2 inline-block rounded px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wider"
                    style={{
                      background:
                        "color-mix(in srgb, var(--color-yes) 15%, transparent)",
                      color: "var(--color-yes)",
                    }}
                  >
                    {t("sessionsList.thisDevice")}
                  </span>
                ) : null}
              </p>
              <p className="mt-0.5 text-[10px] text-[var(--color-text3)]">
                {s.country ? (
                  <span title={s.country}>
                    {countryToFlag(s.country)} {s.country} ·{" "}
                  </span>
                ) : null}
                {s.ip ?? t("sessionsList.unknownIp")} ·{" "}
                {t("sessionsList.lastUsed")} {formatRelativeTime(s.lastUsedAt)} ·{" "}
                {t("sessionsList.expires")} {formatRelativeTime(s.expiresAt)}
              </p>
            </div>
            <button
              type="button"
              disabled={state.revokingId === s.id}
              onClick={() => handleRevoke(s.id)}
              className="rounded border border-[var(--color-border)] px-2 py-1 text-[10px] font-semibold text-[var(--color-text2)] transition-colors hover:border-[var(--color-no)] hover:text-[var(--color-no)] disabled:opacity-50"
            >
              {state.revokingId === s.id
                ? t("sessionsList.revoking")
                : t("sessionsList.revoke")}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * STORY-111c — render an ISO 3166-1 alpha-2 code as the corresponding
 * regional-indicator-symbol emoji flag.
 *
 * Each letter A..Z maps to U+1F1E6..U+1F1FF; the two together render
 * as a flag on every modern OS except Windows (Windows shows the
 * letters — acceptable fallback). Returns an empty string for any
 * non-2-letter input.
 */
function countryToFlag(code: string): string {
  if (!/^[A-Z]{2}$/.test(code)) return "";
  const A = 0x1f1e6;
  const offset = "A".charCodeAt(0);
  return String.fromCodePoint(
    A + (code.charCodeAt(0) - offset),
    A + (code.charCodeAt(1) - offset),
  );
}

function inferBrowser(ua: string): string {
  if (!ua) return "Unknown browser";
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua)) return "Safari";
  return "Browser";
}

function inferOs(ua: string): string {
  if (!ua) return "unknown OS";
  if (/Windows NT/.test(ua)) return "Windows";
  if (/Mac OS X/.test(ua)) return "macOS";
  if (/Android/.test(ua)) return "Android";
  if (/iPhone|iPad/.test(ua)) return "iOS";
  if (/Linux/.test(ua)) return "Linux";
  return "device";
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = now - then;
  const future = diff < 0;
  const abs = Math.abs(diff);
  const sec = Math.floor(abs / 1000);
  const fmt = (n: number, unit: string) =>
    future ? `in ${n}${unit}` : `${n}${unit} ago`;
  if (sec < 60) return future ? `in ${sec}s` : `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return fmt(min, "m");
  const hr = Math.floor(min / 60);
  if (hr < 24) return fmt(hr, "h");
  const d = Math.floor(hr / 24);
  return fmt(d, "d");
}
